import type { FC, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import SystemDialog from "@/ui/Popups/Dialog";

type SaveAsDefaultDialogProps = {
	open: boolean;
	onClose: () => void;
	onConfirm: () => void | Promise<void>;
	/**
	 * Root test ID; `SystemDialog` derives `-confirm-button` and `-cancel-button` from it.
	 *
	 * Optional because five surfaces share this dialog and each needs its OWN prefix to be
	 * distinguishable in a test — one shared literal here would make them ambiguous. Callers that pass
	 * nothing render as before, with no selectors.
	 */
	dataTestId?: string;
	/** A consequence this particular save has on top of the usual one (e.g. it replaces everyone's job colours). */
	extraNotice?: string;
	/**
	 * The whole explanation, in place of the standard sentence (and of `extraNotice`) — for a save whose
	 * consequences need more than one line, or have to be looked up when the dialog opens (the printout
	 * popup's saved printouts). Absent for every other caller, which renders exactly as before. It is still
	 * the dialog's accessible description.
	 */
	body?: ReactNode;
	/** The save is on its way: the confirm button spins and can't be pressed again. Absent = as before. */
	confirmLoading?: boolean;
};

export const SaveAsDefaultDialog: FC<SaveAsDefaultDialogProps> = ({ open, onClose, onConfirm, dataTestId, extraNotice, body, confirmLoading }) => {
	const { t } = useTranslation();
	const standardBody = extraNotice ? `${t("settings.save.as.default.confirm.body")} ${extraNotice}` : t("settings.save.as.default.confirm.body");

	return (
		<SystemDialog
			open={open}
			onClose={onClose}
			title={t("settings.save.as.default.confirm.title")}
			// DEFAULT: no custom explanation means the standard one — what every caller before this prop got.
			body={body ?? standardBody}
			onConfirm={onConfirm}
			dataTestId={dataTestId}
			// Only a custom body needs this; a string body is the description already, so other callers are unchanged.
			describeBody={body !== undefined}
			confirmLoading={confirmLoading}
			confirmDisabled={confirmLoading}
		/>
	);
};
